import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  addChildToObject,
  createObjectShare,
  createStorageObject,
  deleteChildObject,
  deleteObjectShare,
  deleteStorageObject,
  getAllStorageObjects,
  getObjectsShare,
  getStorageObjectById,
  newUpdateStorageObject,
  updateObjectShare,
} from "../../../../services/api/instances/storageApi/methods";
import type {
  StorageObjectValue,
  StorageScope,
  TObjectShareRequest,
  TObjectShareResponse,
  TObjectShareUpdateRequest,
} from "../../../../services/api/instances/storageApi/types/service_types";
import i18n from "../../../../services/i18n";
import type { RootState } from "../../../../services/redux/types";
import {
  clearServiceState,
  removeStorageObjectLocal,
  setIsServiceLoading,
  setSelectedObjectId,
} from "../slice";
import { findNextNavigableObject } from "../utils/findNextNavigableObject";
import { getCountOfFolders, getCountOfObjects } from "../utils/getCount";
import { normalizeLeafStorageObject } from "../utils/normolizeObject";
import {
  NormalizedStorageObject,
  ObjectType,
  PostStorageObject,
} from "../../../../services/api/instances/storageApi/types/object_types";
import { asyncPool } from "../utils/asyncPoll";
import {
  copyStorageObjectStrategies,
  CopyStorageScope,
  defaultCopyStrategy,
} from "../utils/copyStorageObjectStrategies";
import {
  AssistantInfo,
  AssistantValue,
} from "../../../../services/api/instances/assistantsApi/types";
import { ASSISTANT_INITIAL_VALUE } from "../../../../features/Assistants/Constants";
import { getError } from "../../../../services/lib/getError";
import { createNotification } from "../../notificationSlice/slice";
import { getSharedFolderChildIds } from "../utils/getSharedFolderChildIds";
import { clearLastSelectedId } from "../utils/clearLastSelectedId";
import { setLastSelectedId } from "../utils/setLastSelectedId";
import { toBackendDate } from "../utils/toBackendDate";
import axios from "axios";
import { generateId } from "../../../../shared/utils/generateId";

export const fetchAllStorageObjects = createAsyncThunk(
  "storage/fetchAll",
  async (
    {
      service,
      ignoreCondition,
      withRefresh,
    }: {
      service: StorageScope;
      ignoreCondition?: boolean;
      withRefresh?: boolean;
    },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const { selectedId } = (getState() as RootState).storageObjectState[
        service
      ];
      const objects = await getAllStorageObjects(service);
      if (withRefresh) {
        const newObject =
          objects.find((o) => o.id === selectedId) ??
          objects
            .flatMap((o) => o.children ?? [])
            .find((c) => c.id === selectedId);
        dispatch(
          setSelectedObjectId({
            service,
            id: selectedId,
            newValue: newObject?.value,
          }),
        );
      }
      return objects;
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
  {
    condition({ service, ignoreCondition }, { getState }) {
      if (ignoreCondition) return true;
      const state = getState() as RootState;
      const serviceState = state.storageObjectState[service];
      if (service === "assistants") return true;
      if (!serviceState || serviceState === undefined) return true;
      if (!serviceState.hasMutation) return false;

      return true;
    },
  },
);

export const fetchStorageObjectById = createAsyncThunk(
  "storage/fetchStorageObjectById",
  async (
    { service, id }: { service: StorageScope; id: string },
    { signal, rejectWithValue },
  ) => {
    try {
      if (signal.aborted) return rejectWithValue("aborted");
      return await getStorageObjectById(service, id, signal);
    } catch (error: unknown) {
      if (
        signal.aborted ||
        axios.isCancel(error) ||
        (error instanceof Error && error.name === "AbortError")
      ) {
        return rejectWithValue("Aborted");
      }
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const addStorageObject = createAsyncThunk(
  "storage/create",
  async (
    {
      name,
      service,
      objectType,
      currentObjectId,
      value,
      withoutCount,
      skipCreateListener,
    }: {
      name: string;
      service: StorageScope;
      objectType: ObjectType;
      currentObjectId?: string | null;
      value?: StorageObjectValue;
      withoutCount?: boolean;
      skipCreateListener?: boolean;
    },
    ThunkApi,
  ) => {
    const state = ThunkApi.getState() as RootState;
    const { entities } = state.storageObjectState[service].data;
    const entityList: NormalizedStorageObject<StorageObjectValue>[] =
      Object.values(entities ?? {});

    const defaultName =
      objectType === "parent"
        ? name
          ? name
          : i18n.t(`${service}.default_folder_name`)
        : i18n.t(name);

    const count =
      objectType === "parent"
        ? getCountOfFolders(entityList)
        : getCountOfObjects(entityList);

    const objectToPost: PostStorageObject = {
      friendly_name: withoutCount
        ? `${defaultName}`
        : `${defaultName} ${count + 1}`,
      value:
        objectType === "parent"
          ? "null"
          : value
            ? JSON.stringify(value)
            : JSON.stringify({}),
    };

    try {
      const result = await createStorageObject(objectToPost, service);
      const normalized = normalizeLeafStorageObject(result);

      const selectedId = currentObjectId
        ? currentObjectId
        : state.storageObjectState[service].selectedId;

      if (objectType === "parent") {
        normalized.parentId = null;
        normalized.childrenIds = [];
      }

      if (selectedId && objectType === "child") {
        const selectedEntity = entities[selectedId];
        if (
          !selectedEntity ||
          ((selectedEntity?.value === null || selectedEntity?.parentId) &&
            !selectedEntity?.owner)
        ) {
          normalized.parentId = null;
        }
        if (selectedEntity?.value === null && selectedEntity.owner) {
          normalized.parentId = selectedId;
        }
        if (selectedEntity?.parentId && selectedEntity.owner) {
          normalized.parentId = selectedEntity.parentId;
          normalized.childrenIds = [];
        }
      }

      ThunkApi.dispatch(
        setSelectedObjectId({
          service: service as StorageScope,
          id: result.id,
          skipSelectHandler: true,
        }),
      );
      setLastSelectedId(result.id, service);
      return normalized;
    } catch (error: unknown) {
      const message = getError(error);
      return ThunkApi.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const editStorageObject = createAsyncThunk(
  "storage/update",
  async (
    {
      id,
      name,
      value,
      service,
      skipUpdateListener,
    }: {
      id: string;
      name: string;
      value: StorageObjectValue;
      service: StorageScope;
      skipUpdateListener?: boolean;
    },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const { data } = (getState() as RootState).storageObjectState[service];
      const targetObject = data.entities[id];

      if (name.trim() === "" || targetObject.friendly_name === "") {
        const message =
          service === "cases"
            ? "Поле 'Название кейса' должно быть заполнено"
            : "Вы не можете сохранить пустое имя";
        dispatch(createNotification("warning", message));
        return;
      }

      if (!targetObject.write) {
        dispatch(setIsServiceLoading({ service, value: false }));
        dispatch(
          createNotification(
            "info",
            i18n.t("common.read_only_object_edit_message"),
          ),
        );
        return;
      }

      const postObj = {
        friendly_name: name,
        value: JSON.stringify(value),
        object_date: toBackendDate(),
      };
      const res = await newUpdateStorageObject(id, postObj, service);
      return normalizeLeafStorageObject(res);
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const removeStorageObject = createAsyncThunk(
  "storage/delete",
  async (
    {
      id,
      service,
    }: {
      id: string;
      service: StorageScope;
    },
    ThunkAPI,
  ) => {
    try {
      const state = ThunkAPI.getState() as RootState;
      const serviceState = state.storageObjectState[service];
      const selectedId = serviceState.selectedId;
      const sharedObjects = state.storageObjectState.sharedObjects.filter(
        (obj) => obj.object_id === Number(id),
      );
      if (!serviceState) throw new Error("Service not found");

      const entityState = serviceState.data;
      const deletedObject = entityState.entities[id];

      if (!deletedObject) throw new Error("Object not found");

      if (sharedObjects) {
        const ids = sharedObjects.map((o) => o.id);
        await ThunkAPI.dispatch(deleteSharedObject({ ids, service }));
      }

      ThunkAPI.dispatch(
        removeStorageObjectLocal({
          service,
          id,
          object: deletedObject,
        }),
      );

      if (deletedObject?.childrenIds.length > 0) {
        await ThunkAPI.dispatch(
          detachChildBeforeDeleteParent({
            childIds: deletedObject.childrenIds,
            parentId: id,
            service,
          }),
        ).unwrap();
      }
      const freshEntityState = serviceState.data;
      await deleteStorageObject(id, service);

      const nextId = findNextNavigableObject(
        freshEntityState.ids,
        freshEntityState.entities,
        id,
      );

      if (nextId) {
        if (selectedId === id) {
          ThunkAPI.dispatch(
            setSelectedObjectId({
              service,
              id: nextId,
            }),
          );
        }
        return { id, nextId };
      } else {
        clearLastSelectedId(service);
        ThunkAPI.dispatch(clearServiceState({ service }));
        return { id, nextId: null };
      }
    } catch (error: unknown) {
      const message = getError(error);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const attachChildToParent = createAsyncThunk(
  "storage/addChild",
  async (
    {
      childIds,
      parentId,
      service,
    }: {
      childIds: string[];
      parentId: string;
      service: StorageScope;
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const results = await Promise.allSettled(
        childIds.map((childId) => addChildToObject(childId, parentId, service)),
      );

      const succeeded = results
        .filter(
          (result): result is PromiseFulfilledResult<null> =>
            result.status === "fulfilled",
        )
        .map((result) => result.value);

      const failed = results
        .filter((result) => result.status === "rejected")
        .map((result) => (result as PromiseRejectedResult).reason);

      if (failed.length > 0) {
        failed.forEach(({ result }) => {
          const reason = (result as PromiseRejectedResult).reason;
          dispatch(
            createNotification(
              "error",
              getError(reason) ?? "Не удалось добавить вложенный объект",
            ),
          );
        });
      }

      return succeeded;
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const detachChildFromParent = createAsyncThunk(
  "storage/removeChild",
  async (
    {
      childIds,
      parentId,
      service,
    }: {
      childIds: string[];
      parentId: string;
      service: StorageScope;
    },
    { rejectWithValue },
  ) => {
    try {
      const results = await Promise.allSettled(
        childIds.map((childId) => deleteChildObject(childId, parentId)),
      );
      const succeeded = results
        .filter(
          (result): result is PromiseFulfilledResult<null> =>
            result.status === "fulfilled",
        )
        .map((result) => result.value);

      const failed = results
        .filter((result) => result.status === "rejected")
        .map((result) => (result as PromiseRejectedResult).reason);

      if (failed.length > 0) {
        const message = failed.map((error) => getError(error)).join("; ");
        return rejectWithValue(message || "An unknown error occurred");
      }

      return { childIds, parentId, service };
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const copyObjectAction = createAsyncThunk(
  "storage/copyObject",
  async (
    {
      id,
      service,
      name,
    }: {
      id: string;
      service: StorageScope;
      name?: string;
    },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const { data } = (getState() as RootState).storageObjectState[service];
      const currentObject = data.entities[id];
      if (!currentObject) return;
      const strategy =
        service in copyStorageObjectStrategies
          ? copyStorageObjectStrategies[service as CopyStorageScope]
          : defaultCopyStrategy;

      const objectValue = strategy(currentObject.value);
      return await dispatch(
        addStorageObject({
          name: name
            ? name
            : `${i18n.t("common.copy_object_prefix")} ${currentObject.friendly_name}`,
          value:
            service === "assistants"
              ? {
                  ...objectValue,
                  assistant_object: {
                    ...(objectValue as AssistantValue).assistant_object,
                    id: `asst_${generateId()}`,
                    thread_id: "",
                  },
                }
              : objectValue,
          service,
          objectType: "child",
          withoutCount: true,
          skipCreateListener: true,
        }),
      ).unwrap();
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const detachChildBeforeDeleteParent = createAsyncThunk(
  "storage/detachChildBeforeDeleteParent",
  async (
    {
      childIds,
      parentId,
      service,
    }: {
      childIds: string[];
      parentId: string;
      service: StorageScope;
    },
    { dispatch, rejectWithValue },
  ) => {
    const CONCURRENCY_LIMIT = 5;

    try {
      await asyncPool(CONCURRENCY_LIMIT, childIds, async (childId) => {
        const resultAction = await dispatch(
          detachChildFromParent({ childIds: [childId], parentId, service }),
        );
        if (detachChildFromParent.rejected.match(resultAction)) {
          console.warn(
            `Не удалось удалить child ${childId}:`,
            resultAction.error,
          );
        }
      });
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const fetchAllStorageObjectsSharing = createAsyncThunk(
  "storage/fetchAllObjectsSharing",
  async (_, { rejectWithValue }) => {
    try {
      return await getObjectsShare();
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createSharedObject = createAsyncThunk(
  "storage/createSharedObject",
  async (
    {
      rawPermissions,
      service,
    }: { rawPermissions: TObjectShareRequest[]; service: StorageScope },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const results = await Promise.allSettled(
        rawPermissions.map((permission) =>
          createObjectShare(permission as TObjectShareRequest, service),
        ),
      );

      const succeeded = results
        .filter(
          (result): result is PromiseFulfilledResult<TObjectShareResponse> =>
            result.status === "fulfilled",
        )
        .map((result) => result.value);

      const failed = results
        .filter((result) => result.status === "rejected")
        .map((result) => (result as PromiseRejectedResult).reason);

      if (failed.length > 0) {
        failed.forEach((error) => {
          const message =
            getError(error) ?? `Ошибка при попытке поделиться объектом}`;
          dispatch(createNotification("error", message, 5000));
        });
      }

      return succeeded;
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateSharedObject = createAsyncThunk(
  "storage/updateSharedObject",
  async (
    permissions: { id: number; putObject: TObjectShareUpdateRequest }[],
    { dispatch, rejectWithValue },
  ) => {
    try {
      const results = await Promise.allSettled(
        permissions.map(({ id, putObject }) =>
          updateObjectShare(putObject as TObjectShareUpdateRequest, id),
        ),
      );

      const succeeded = results
        .filter(
          (result): result is PromiseFulfilledResult<TObjectShareResponse> =>
            result.status === "fulfilled",
        )
        .map((result) => result.value);

      const failed = results
        .filter((result) => result.status === "rejected")
        .map((result) => (result as PromiseRejectedResult).reason);

      if (failed.length > 0) {
        failed.forEach((error) => {
          const message =
            getError(error) ?? `Ошибка при попытке обновления прав доступа}`;
          dispatch(createNotification("error", message, 5000));
        });
      }

      return succeeded;
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteSharedObject = createAsyncThunk(
  "storage/deleteSharedObject",
  async (
    { ids, service }: { ids: number[]; service: StorageScope },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const state = (getState() as RootState).storageObjectState;
      const results = await Promise.allSettled(
        ids.map((id) => deleteObjectShare(id)),
      );

      let successfulIds: number[] = [];
      const errors: string[] = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          const id = ids[index];
          const childIds = getSharedFolderChildIds(
            id,
            state.sharedObjects,
            state[service].data.entities,
          );
          successfulIds = [...successfulIds, ...childIds];
        } else {
          const message =
            getError(result.reason) ?? `Failed to delete id ${ids[index]}`;
          dispatch(createNotification("error", message, 5000));
        }
      });

      if (successfulIds.length === 0) {
        return rejectWithValue(errors.join(", "));
      }

      return { successfulIds };
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
